import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, Link2, Loader2, Maximize2, Search, X } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { PET_CARE_TOPICS, SOON } from "@/lib/sites";

// Learn: step-by-step guides with pictures (How to) and pet care tips (coming
// soon). The guides come from the server (/api/learn). Customer guides are
// L-1xx, business guides L-2xx; staff guides live only in the console.

const FOR = [["customer", "For customers"], ["business", "For businesses"]];
const ROLES = [["", "All businesses"], ["shop", "Shops"], ["care", "Care providers"], ["ngo", "NGOs"], ["rider", "Delivery partners"]];
const FOR_LABEL = { customer: "For customers", business: "For businesses" };

export const guideImage = (file) => `${API_BASE}/learn/img/${file}`;

// Which app a guide's "In the app" steps are for.
const APP_NAME = { customer: "PawBazaar app", partner: "Partner app", rider: "Rider app" };
const WHERE_KEY = "learn.where";

/** Website or app steps: remembered on this device, and ?in=app in the link. */
function useWhere() {
  const [params] = useSearchParams();
  const [where, setWhere] = useState(() => {
    if (params.get("in") === "app" || params.get("in") === "web") return params.get("in");
    try { return localStorage.getItem(WHERE_KEY) || "web"; } catch { return "web"; }
  });
  const pick = (w) => { setWhere(w); try { localStorage.setItem(WHERE_KEY, w); } catch { /* private mode */ } };
  return [where, pick];
}

/** The whole picture over the page: fits the screen; tap it to see it at full size and scroll around. */
function PictureViewer({ src, alt, onClose }) {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={alt} className="fixed inset-0 z-[100] flex flex-col" style={{ background: "rgba(12,10,9,0.96)" }} data-testid="learn-viewer">
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-white text-sm">
        <span>{full ? "Full size: scroll to move around. Tap to fit the screen." : "Tap the picture to see it at full size."}</span>
        <button type="button" onClick={onClose} className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center" aria-label="Close"><X className="h-5 w-5" /></button>
      </div>
      <div className={`flex-1 overflow-auto ${full ? "" : "flex items-center justify-center"} p-3`} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <img src={src} alt={alt} onClick={() => setFull((x) => !x)}
          className={full ? "max-w-none cursor-zoom-out" : "max-w-full max-h-full object-contain cursor-zoom-in rounded-xl"} />
      </div>
    </div>
  );
}

/** A step's picture. Computer-size pictures show large; on a phone they show
 *  the close-up of the marked button, and the whole screen opens in the
 *  viewer. Tap any picture to open it full screen. */
function GuidePicture({ s, alt }) {
  const [open, setOpen] = useState(false);
  const src = guideImage(s.img);
  const pic = (url, cls) => (
    <button type="button" onClick={() => setOpen(true)} className={`group relative block w-full ${cls}`} aria-label={`${alt}. Open the picture full screen`}>
      <img src={url} alt={alt} loading="lazy" className="w-full rounded-2xl border border-stone-200 bg-stone-100" />
      <span className="absolute right-2 bottom-2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-80 group-hover:opacity-100" aria-hidden>
        <Maximize2 className="h-4 w-4" /></span>
    </button>
  );
  return (
    <div className="space-y-2">
      {s.phone ? pic(src, "max-w-[20rem] mx-auto")
        : s.zoom ? <>
          {pic(guideImage(s.zoom), "sm:hidden")}
          {pic(src, "hidden sm:block")}
          <button type="button" onClick={() => setOpen(true)} className="sm:hidden text-sm font-semibold text-teal">See the whole screen</button>
        </> : pic(src, "")}
      {open && <PictureViewer src={src} alt={alt} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** A guide's text: **bold**, and {L-110} becomes a link to that guide. */
export function GuideText({ text }) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*|\{[LSA]-\d{3}\})/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <b key={i}>{p.slice(2, -2)}</b>;
    const ref = p.match(/^\{([LSA]-\d{3})\}$/);
    if (ref) return <Link key={i} to={`/learn/${ref[1]}`} className="font-semibold text-teal underline-offset-2 hover:underline">{ref[1]}</Link>;
    return <Fragment key={i}>{p}</Fragment>;
  });
}

function Tabs({ tab, setTab }) {
  return (
    <div role="tablist" aria-label="Learn" className="grid grid-cols-2 sm:flex gap-1.5 text-[15px]">
      {[["how-to", "How to"], ["pet-care", "Pet care"]].map(([k, label]) => (
        <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`learn-tab-${k}`}
          className={`h-11 px-5 rounded-full ${tab === k ? "bg-white border border-stone-200 font-semibold shadow-sm" : "text-stone-600 hover:text-stoneWarm"}`}>{label}</button>
      ))}
    </div>
  );
}

export function LearnHome() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "pet-care" ? "pet-care" : "how-to";
  const audience = params.get("for") === "business" ? "business" : "customer";
  const role = params.get("role") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [rows, setRows] = useState(null);
  const set = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    if (k === "for") p.delete("role");
    setParams(p, { replace: true });
  };
  useEffect(() => { document.title = "Learn · PawBazaar"; }, []);
  useEffect(() => {
    if (tab !== "how-to") return undefined;
    let alive = true;
    const t = setTimeout(() => {
      api.get("/learn/guides", { params: { audience, role: role || undefined, q: q.trim() || undefined } })
        .then(({ data }) => alive && setRows(data)).catch(() => alive && setRows([]));
    }, q ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [tab, audience, role, q]);
  const groups = useMemo(() => {
    const out = [];
    (rows || []).forEach((g) => {
      let grp = out.find((x) => x.name === (g.group || "Other"));
      if (!grp) out.push(grp = { name: g.group || "Other", items: [] });
      grp.items.push(g);
    });
    return out;
  }, [rows]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="learn-home">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold">Learn</h1>
          <p className="text-stone-600 mt-1">{tab === "how-to" ? "Step-by-step guides with pictures, for everything on PawBazaar." : "Tips for a happier, healthier pet."}</p>
        </div>
        <Tabs tab={tab} setTab={(k) => set("tab", k === "how-to" ? "" : k)} />
      </div>

      {tab === "pet-care" ? (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="learn-pet-care">
          {PET_CARE_TOPICS.map(([slug, line]) => (
            <div key={slug} className="rounded-3xl border border-stone-200 bg-white p-5">
              <div className="font-semibold text-lg">{SOON[slug]}</div>
              <div className="text-sm text-stone-600 mt-1">{line}</div>
              <span className="inline-block mt-3 text-xs font-bold rounded-full px-2.5 py-1 bg-sky-100 text-sky-800">Coming soon</span>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-col md:flex-row gap-3">
            <label className="flex-1 flex items-center gap-2 h-12 px-4 rounded-full border border-stone-300 bg-white focus-within:border-stoneWarm">
              <Search className="h-4 w-4 text-stone-400" aria-hidden />
              <span className="sr-only">Search guides</span>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guides, for example: add a pet, cancel an order, payouts"
                className="flex-1 min-w-0 bg-transparent outline-none text-[15px]" data-testid="learn-search" />
            </label>
            <div className="grid grid-cols-2 gap-1 rounded-full bg-white border border-stone-200 p-1 text-sm font-semibold">
              {FOR.map(([k, label]) => (
                <button key={k} type="button" aria-pressed={audience === k} onClick={() => set("for", k === "customer" ? "" : k)} data-testid={`learn-for-${k}`}
                  className={`h-10 px-4 rounded-full ${audience === k ? "bg-stoneWarm text-white" : "text-stone-700 hover:bg-stone-100"}`}>{label}</button>
              ))}
            </div>
          </div>
          {audience === "business" && (
            <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Type of business">
              {ROLES.map(([k, label]) => (
                <button key={k || "all"} type="button" aria-pressed={role === k} onClick={() => set("role", k)}
                  className={`h-10 px-4 rounded-full border text-sm ${role === k ? "border-stoneWarm bg-stoneWarm text-white" : "border-stone-300 bg-white hover:border-stoneWarm"}`}>{label}</button>
              ))}
            </div>
          )}
          {rows === null ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>
            : !rows.length ? <p className="mt-10 text-stone-600" data-testid="learn-empty">{q ? `No guides match "${q}". Try other words.` : "Guides for this are on their way."}</p>
              : (
                <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groups.map((grp) => (
                    <section key={grp.name} className="rounded-3xl border border-stone-200 bg-white px-5 py-4">
                      <h2 className="font-semibold text-[17px] mb-1">{grp.name}</h2>
                      <ul>
                        {grp.items.map((g) => (
                          <li key={g.id}>
                            <Link to={`/learn/${g.id}`} className="flex justify-between gap-3 py-2.5 text-[15px] text-teal hover:text-stoneWarm" data-testid={`learn-guide-${g.id}`}>
                              <span>{g.title}</span><span className="text-xs text-stone-500 shrink-0 pt-0.5">{g.id}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              )}
        </>
      )}
      <p className="mt-10 text-sm text-stone-600">Still stuck? <Link to="/help-centre" className="font-semibold text-teal">Go to Help</Link> to report a problem or contact us.</p>
    </div>
  );
}

function Feedback({ id }) {
  const [sent, setSent] = useState(null);
  const send = async (helpful) => {
    setSent(helpful);
    try { await api.post(`/learn/guides/${id}/feedback`, { helpful }); } catch { /* counted best-effort */ }
  };
  if (sent !== null) return <span className="text-stone-700">{sent ? "Thanks for telling us." : "Thanks. We will make this guide clearer."}</span>;
  return (
    <span className="flex items-center gap-2">Did this guide help?
      <button type="button" onClick={() => send(true)} className="h-9 px-4 rounded-full border border-stone-300 bg-white hover:border-stoneWarm">Yes</button>
      <button type="button" onClick={() => send(false)} className="h-9 px-4 rounded-full border border-stone-300 bg-white hover:border-stoneWarm">No</button>
    </span>
  );
}

export function GuidePage() {
  const { id } = useParams();
  const [g, setG] = useState(null);
  const [missing, setMissing] = useState(false);
  const [where, setWhere] = useWhere();
  useEffect(() => {
    setG(null); setMissing(false);
    api.get(`/learn/guides/${id}`).then(({ data }) => { setG(data); document.title = `${data.title} · Learn · PawBazaar`; window.scrollTo(0, 0); })
      .catch(() => setMissing(true));
  }, [id]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy. Copy the address from the browser bar."); }
  };
  if (missing) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <h1 className="font-heading text-2xl font-semibold">Guide not found</h1>
      <p className="text-stone-600 mt-2">This guide may have moved. <Link to="/learn" className="font-semibold text-teal">See all guides</Link></p>
    </div>
  );
  if (!g) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>;
  // A guide may have steps for the website, for the app, or both (then a switch shows).
  const hasWeb = (g.steps || []).length > 0;
  const hasApp = (g.app_steps || []).length > 0;
  const shown = hasApp && (where === "app" || !hasWeb) ? "app" : "web";
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid lg:grid-cols-[15rem_1fr] gap-6 lg:gap-8 items-start" data-testid="learn-guide">
      <aside className="rounded-3xl border border-stone-200 bg-white p-4 text-sm lg:sticky lg:top-24">
        <Link to={`/learn${g.audience === "business" ? "?for=business" : ""}`} className="inline-flex items-center gap-1 font-semibold text-teal"><ArrowLeft className="h-4 w-4" aria-hidden />Back to Learn</Link>
        {g.same_group?.length > 1 && (
          <nav aria-label={g.group} className="hidden lg:block mt-3">
            <div className="text-xs font-bold tracking-widest text-stone-500 uppercase mb-1">{g.group}</div>
            {g.same_group.map((x) => (x.id === g.id
              ? <span key={x.id} aria-current="page" className="block px-2.5 py-1.5 rounded-xl bg-marigold/15 text-terracotta font-bold">{x.title}</span>
              : <Link key={x.id} to={`/learn/${x.id}`} className="block px-2.5 py-1.5 rounded-xl hover:bg-stone-100">{x.title}</Link>))}
          </nav>
        )}
      </aside>
      <article className="min-w-0 space-y-5">
        <header className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <span className="text-xs font-bold text-teal bg-teal/10 rounded-full px-2.5 py-1">{g.id} · {FOR_LABEL[g.audience]}</span>
            <h1 className="font-heading text-3xl font-semibold mt-3">{g.title}</h1>
            {g.summary && (
              <p className="text-stone-600 mt-1"><GuideText text={g.summary} />
                {g.minutes ? <span className="inline-flex items-center gap-1 ml-2 text-sm"><Clock className="h-3.5 w-3.5" aria-hidden />About {g.minutes} min</span> : null}
              </p>
            )}
          </div>
          <button type="button" onClick={copy} className="h-11 px-4 rounded-full border border-stone-300 bg-white text-sm font-semibold inline-flex items-center gap-2 shrink-0 self-start" data-testid="learn-copy-link">
            <Link2 className="h-4 w-4" aria-hidden />Copy link
          </button>
        </header>
        {g.needs && <div className="rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-700"><b>You need:</b> <GuideText text={g.needs} /></div>}
        {hasApp && hasWeb && (
          <div role="tablist" aria-label="Where you do it" className="inline-flex rounded-full bg-stone-100 p-1" data-testid="learn-where">
            {[["web", "On the website"], ["app", `In the ${APP_NAME[g.app] || "app"}`]].map(([v, l]) => (
              <button key={v} type="button" role="tab" aria-selected={shown === v} onClick={() => setWhere(v)}
                className={`h-10 px-4 rounded-full text-sm font-semibold ${shown === v ? "bg-white shadow-sm text-stone-900" : "text-stone-600"}`}>{l}</button>
            ))}
          </div>
        )}
        <ol className="space-y-4">
          {(shown === "app" ? g.app_steps : g.steps || []).map((s, i) => (
            <li key={i} className="rounded-3xl border border-stone-200 bg-white p-4 sm:p-5 space-y-4" data-testid={`learn-step-${i + 1}`}>
              <div className="flex gap-3 md:gap-4">
                <span className="h-9 w-9 shrink-0 rounded-full bg-stoneWarm text-white font-bold flex items-center justify-center" aria-hidden>{i + 1}</span>
                <div>
                  <h2 className="font-semibold text-[17px]"><span className="sr-only">Step {i + 1}: </span>{s.title}</h2>
                  {s.text && <p className="text-[15px] text-stone-700 mt-1 whitespace-pre-line"><GuideText text={s.text} /></p>}
                  {s.tip && <p className="text-sm text-amber-900 bg-amber-100 rounded-xl px-3 py-2 mt-2"><b>Tip:</b> <GuideText text={s.tip} /></p>}
                </div>
              </div>
              {s.img && <GuidePicture s={s} alt={s.alt || `Step ${i + 1}: ${s.title}`} />}
            </li>
          ))}
        </ol>
        {g.related?.length > 0 && (
          <section className="rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm">
            <b>Next:</b> {g.related.map((r, i) => <Fragment key={r.id}>{i > 0 && " · "}<Link to={`/learn/${r.id}`} className="font-semibold text-teal">{r.title}</Link></Fragment>)}
          </section>
        )}
        <footer className="rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <Feedback id={g.id} />
          <span className="text-stone-500">{g.checked ? `Last checked: ${g.checked} · ` : ""}Still stuck? <Link to="/help-centre" className="font-semibold text-teal">Go to Help</Link></span>
        </footer>
      </article>
    </div>
  );
}
